/**
 * Purpose:
 *   Provide the toast system client boundary and global event bridge.
 */
'use client';

import { useEffect } from 'react';
import { ToastProvider } from '../context/ToastContext';
import { useToast } from '../hooks/useToast';

/**
 * ToastSystem
 * -----------
 * Single client boundary that:
 * 1) Provides the ToastContext for the whole app.
 * 2) Reads a server-written cookie with a one-shot toast on mount (optional).
 * 3) Subscribes to a small global event bus so any client module can fire a toast.
 *
 * Keep this mounted once under your server layout.
 */

export type ToastType = 'error' | 'warning' | 'success';

export type ToastPayload = {
  message: string;
  type: ToastType;
  duration?: number;
};

/* -------------------------- Tiny global event bus -------------------------- */
type Listener = (p: ToastPayload) => void;
const listeners = new Set<Listener>();

export const toastBus = {
  emit(payload: ToastPayload) {
    listeners.forEach((l) => l(payload));
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      // cleanup must be () => void (do not return Set.delete boolean)
      listeners.delete(fn);
    };
  },
};

/* -------- Server → Client hydration: read & clear one-shot cookie ---------- */
function readAndClearToastCookie(): ToastPayload | null {
  try {
    const entry = document.cookie
      .split('; ')
      .find((c) => c.startsWith('x-toast='));
    if (!entry) return null;

    // Clear immediately to avoid repeats on refresh
    document.cookie = 'x-toast=; Max-Age=0; Path=/; SameSite=Lax';

    const [, raw] = entry.split('=');
    const payload = JSON.parse(decodeURIComponent(raw));
    if (
      payload &&
      typeof payload.message === 'string' &&
      (payload.type === 'error' ||
        payload.type === 'warning' ||
        payload.type === 'success')
    ) {
      return payload as ToastPayload;
    }
  } catch {
    // ignore malformed cookie
  }
  return null;
}

export function ToastSystem({ children }: { children: React.ReactNode }) {
  return (
    <ToastProvider>
      <Effects />
      {children}
    </ToastProvider>
  );
}

/** Internal side-effects (live inside the provider so `useToast()` has context). */
function Effects() {
  const { addToast } = useToast();

  useEffect(() => {
    // 1) Hydrate server-triggered toast (if present)
    const cookie = readAndClearToastCookie();
    if (cookie) addToast(cookie);

    // 2) Bridge the bus → addToast
    return toastBus.subscribe(addToast);
  }, [addToast]);

  return null;
}
